import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import constants from '../constants';
import getUrlPrefix from './getUrlPrefix';
import axios from '../axios';
import { notify } from '../Notification';

export type OfferingData = {
  id: string;
  successor_offering_id: string;
  plan_name?: string;
};

export const PlanType = {
  Standard: 'standard',
  Custom: 'custom',
  PCP: 'progressive',
  EmployerMembership: 'emp',
};

const PlanTypeChoices = {
  [PlanType.Standard]: 'Standard Plan',
  [PlanType.Custom]: 'Custom Plan',
  [PlanType.PCP]: 'Progressive Cash Plan',
  [PlanType.EmployerMembership]: 'Employer Membership Plan',
};

const planTypeOptions = Object.entries(PlanTypeChoices);

export function useSuccessorOfferings({
  portal,
  employerId,
  planYearId,
}: {
  portal: string;
  employerId?: string;
  planYearId: string;
}) {
  const queryClient = useQueryClient();

  const successorOfferingsQuery = useQuery(
    ['successor-offerings', planYearId, employerId],
    () => {
      const baseUrl = `${getUrlPrefix(portal)}/plan-year/${planYearId}/get-successor-offerings/`;
      const url =
        portal === constants.PORTALS.ADMIN ? `${baseUrl}?corporation_pk=${employerId}` : baseUrl;
      return axios.get(url).then((response: any) => response.data.data);
    },
    {
      placeholderData: {},
      enabled: portal === constants.PORTALS.ADMIN ? !!planYearId && !!employerId : !!planYearId,
    }
  );

  const updateSuccessorOfferingsMutation = useMutation(
    ({ plan_year_id, successors }: { plan_year_id: string; successors: OfferingData[] }) =>
      axios.put(`${getUrlPrefix(portal)}/plan-year/${plan_year_id}/update-successor-offerings/`, {
        last_year_offerings: successors,
      }),
    {
      onSuccess: (response: any) => {
        notify('Updated plan transitions.', 'success');
        queryClient.invalidateQueries(['successor-offerings', planYearId, employerId]);
      },
      onError: (error: Error) => {
        notify(error.message, 'error');
      },
    }
  );

  return {
    successorOfferings: successorOfferingsQuery.data,
    updateSuccessors: updateSuccessorOfferingsMutation.mutateAsync,
  };
}

// Fetch plans
export const useFetchPlans = ({
  portal,
  employerId,
  planYearId,
}: {
  portal: string;
  employerId?: string;
  planYearId: string;
}) => {
  const queryClient = useQueryClient();

  const plansQuery = useQuery(
    ['plans', planYearId, employerId],
    () => {
      const baseUrl = `${getUrlPrefix(portal)}/plan/get-plans-data/?plan_year=${planYearId}`;
      const url =
        portal === constants.PORTALS.ADMIN ? `${baseUrl}&corporation_pk=${employerId}` : baseUrl;
      return axios.get(url).then((response: any) => response.data.data);
    },
    {
      placeholderData: {},
      enabled: portal === constants.PORTALS.ADMIN ? !!planYearId && !!employerId : !!planYearId,
    }
  );

  const planType = plansQuery.data?.plan_type;

  return {
    planLoading: plansQuery.isLoading,
    planType,
    standardPlans: planType === PlanType.Standard ? plansQuery.data?.plans : [],
    customPlans: planType === PlanType.Custom ? plansQuery.data?.plans : [],
    progressivePlans: planType === PlanType.PCP ? plansQuery.data?.plans : [],
    employerMembershipPlan:
      planType === PlanType.EmployerMembership ? plansQuery.data?.plans?.[0] : null,
    createPlansEnabled: plansQuery.data?.create_plans_enabled || false,
    createPlansDisabledMessage: plansQuery.data?.create_plans_disabled_message,
    switchingPlanTypeEnabled: plansQuery.data?.switching_plan_type_enabled,
    switchingPlanTypeDisabledMessage: plansQuery.data?.switching_plan_type_disabled_message,
    updateTransitionsEnabled: plansQuery.data?.update_transitions_enabled,
    updateTransitionsDisabledMessage: plansQuery.data?.update_transitions_disabled_message,
    refreshPlans: () => {
      queryClient.invalidateQueries(['plans']);
      return Promise.resolve(true); // for promise chaining, possible TODO: obviate this by using async in callers instead
    },
  };
};

export function useEmployerPlanValues(
  open: boolean = false,
  currentInOfficePlanId: string | number | null | undefined = null
): [string | null, Dispatch<SetStateAction<string | null>>, (submit: () => void) => void] {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // on open, pre-select whichever in-office plan is currently in use, if any,
  // so the user sees and can change the existing selection rather than
  // starting from a blank search
  useEffect(() => {
    if (open) {
      setSelectedPlanId(
        currentInOfficePlanId !== null && currentInOfficePlanId !== undefined
          ? String(currentInOfficePlanId)
          : null
      );
    }
  }, [open, currentInOfficePlanId]);

  function canSubmitEmployerPlan(submit: () => void) {
    if (selectedPlanId) {
      submit();
    }
  }

  return [selectedPlanId, setSelectedPlanId, canSubmitEmployerPlan];
}

export default {
  PlanType,
  PlanTypeChoices,
  planTypeOptions,
  useEmployerPlanValues,
  useSuccessorOfferings,
  useFetchPlans,
};
